import React from 'react';
import { IHostViewport, scrollIntoView } from '../../../frame-messages';

/**
 * Where the area's pinned parts go while a screen scrolls. Without a host report they are left to the
 * stylesheet, which sticks them to the body of a fixed-height frame: the navigation to its top, a
 * screen's pinned header bar `navigationHeight` below. Where the host page reports that it scrolls the
 * frame with its own page (`hostScrollsFrame`), nothing in the frame scrolls, so they are moved to the
 * band of the frame the host shows instead: `hostTop` down the frame and reaching `hostHeight` down
 * from there (both 0 before the host's first report arrives). Either way the pinned parts cover the
 * top `pinnedHeight` of the visible area: the navigation and, while it is pinned, the header bar, whose
 * height the screen's header reports through `onPinnedHeaderHeight` (0 while the bar is not shown).
 */
export interface IBrandPresencePinnedArea {
  hostScrollsFrame: boolean;
  hostTop: number;
  hostHeight: number;
  navigationHeight: number;
  pinnedHeight: number;
  onPinnedHeaderHeight: (height: number) => void;
}

export const BrandPresencePinnedAreaContext = React.createContext<IBrandPresencePinnedArea>({
  hostScrollsFrame: false,
  hostTop: 0,
  hostHeight: 0,
  navigationHeight: 0,
  pinnedHeight: 0,
  onPinnedHeaderHeight: () => undefined,
});

const PINNED_PART_ATTRIBUTE = 'data-brand-presence-pinned';

/**
 * Spread onto a part of the area pinned over the top of the visible area: the navigation, and a
 * screen's header bar while it is pinned. A focus inside it moves with the part and is left where it is.
 */
export const BRAND_PRESENCE_PINNED_PART_PROPS: Readonly<Record<string, string>> = { [PINNED_PART_ATTRIBUTE]: '' };

/**
 * How long after the keyboard moved the focus the area still brings the focused element clear of the
 * pinned parts when they or the host's band change: time for the host to scroll and for the header bar
 * to pin once the header has left the visible area.
 */
const FOCUS_REVEAL_WINDOW_MS = 1_000;

/** What a reader does to scroll the screen: a wheel, a touch that moves. A key is heard anywhere in the frame. */
const READER_SCROLL_EVENTS = ['wheel', 'touchmove'] as const;

/**
 * How long after a finger touches the screen the focus it gives still counts as the pointer's: a tap
 * gives its focus once the finger lifts, after the press itself was handled.
 */
const TAP_FOCUS_WINDOW_MS = 1_000;

/** The pinned parts' place in the frame, as the frame last drew them. */
interface IPinnedBand {
  top: number;
  pinnedHeight: number;
}

/**
 * Keeps an element the keyboard moves the focus to clear of the parts the area pins, where the host
 * scrolls the frame. The host scrolls a focused element into its visible area but knows nothing of the
 * bars the frame lays over the top of that area, so the element can come to rest under them. The host
 * is asked to bring the element into view below the pinned parts instead (`scroll-into-view`): one
 * frame after the focus moved, and again within a moment of that whenever the pinned parts or the
 * host's band change while the element lies under the pinned parts, as it does when the header bar
 * pins once the host's scroll has taken the header out of view. Nothing is asked again for an element
 * no longer under the pinned parts, nor once the reader scrolls the frame, presses a key or presses a
 * pointer in it. A scroll the host makes on its own, with its scroll bar for one, reaches the frame
 * only as the band moving, so an element it leaves under the pinned parts within that moment is
 * brought clear once. A focus a pointer press gave, a tap included, and a focus inside a pinned part,
 * which moves with the part, leave the host where it is.
 */
export function useFocusClearOfPinnedParts({
  areaRef,
  hostScrollsFrame,
  hostTop,
  pinnedHeight,
}: {
  areaRef: React.RefObject<HTMLElement | null>;
  hostScrollsFrame: boolean;
  hostTop: number;
  pinnedHeight: number;
}): void {
  const focusRef = React.useRef<{ element: Element; until: number } | undefined>(undefined);
  const requestRef = React.useRef<{ frame: number; whenCovered: boolean } | undefined>(undefined);
  const bandRef = React.useRef<IPinnedBand>({ top: hostTop, pinnedHeight });
  bandRef.current = { top: hostTop, pinnedHeight };

  // One frame later: the focus has settled, the host has had the focus's own scroll, and the pinned
  // parts are drawn where the latest band puts them. `whenCovered` asks only while the element lies
  // under the pinned parts; a request made without it asks in any case.
  const requestReveal = React.useCallback((whenCovered: boolean) => {
    if (requestRef.current) {
      requestRef.current.whenCovered = requestRef.current.whenCovered && whenCovered;
      return;
    }
    const request = {
      whenCovered,
      frame: window.requestAnimationFrame(() => {
        requestRef.current = undefined;
        const focus = focusRef.current;
        if (!focus || focus.element !== document.activeElement || Date.now() > focus.until) {
          return;
        }
        const rect = focus.element.getBoundingClientRect();
        const band = bandRef.current;
        const covered = rect.top < band.top + band.pinnedHeight && rect.bottom > band.top;
        if (request.whenCovered && !covered) {
          return;
        }
        scrollIntoView({ top: Math.floor(rect.top - band.pinnedHeight), bottom: Math.ceil(rect.bottom) });
      }),
    };
    requestRef.current = request;
  }, []);

  React.useEffect(() => {
    const area = areaRef.current;
    if (!hostScrollsFrame || !area) {
      return;
    }
    // A mouse or a pen gives its focus while its press is handled; a finger once it lifts, within
    // TAP_FOCUS_WINDOW_MS. A key, wherever in the frame it is pressed, ends the press at once.
    let pointerPress: { until: number } | undefined;
    let pressHandled: ReturnType<typeof setTimeout> | undefined;
    const onPointerDown = (event: PointerEvent) => {
      focusRef.current = undefined;
      clearTimeout(pressHandled);
      if (event.pointerType === 'touch') {
        pointerPress = { until: Date.now() + TAP_FOCUS_WINDOW_MS };
        return;
      }
      pointerPress = { until: Number.POSITIVE_INFINITY };
      pressHandled = setTimeout(() => {
        pointerPress = undefined;
      });
    };
    // Once the reader scrolls or presses a key, the page is theirs: nothing is asked again for the
    // element the keyboard moved to before. A Tab moves the focus on, and the next element is asked for.
    const onReaderScroll = () => {
      focusRef.current = undefined;
    };
    const onKeyDown = () => {
      focusRef.current = undefined;
      pointerPress = undefined;
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      const pointerFocus = pointerPress !== undefined && Date.now() <= pointerPress.until;
      if (pointerFocus || !(target instanceof Element) || target.closest(`[${PINNED_PART_ATTRIBUTE}]`)) {
        focusRef.current = undefined;
        return;
      }
      focusRef.current = { element: target, until: Date.now() + FOCUS_REVEAL_WINDOW_MS };
      requestReveal(false);
    };
    area.addEventListener('pointerdown', onPointerDown, { capture: true });
    area.addEventListener('focusin', onFocusIn);
    for (const type of READER_SCROLL_EVENTS) {
      area.addEventListener(type, onReaderScroll, { capture: true, passive: true });
    }
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      area.removeEventListener('pointerdown', onPointerDown, { capture: true });
      area.removeEventListener('focusin', onFocusIn);
      for (const type of READER_SCROLL_EVENTS) {
        area.removeEventListener(type, onReaderScroll, { capture: true });
      }
      document.removeEventListener('keydown', onKeyDown, { capture: true });
      clearTimeout(pressHandled);
      focusRef.current = undefined;
      if (requestRef.current) {
        window.cancelAnimationFrame(requestRef.current.frame);
        requestRef.current = undefined;
      }
    };
  }, [areaRef, hostScrollsFrame, requestReveal]);

  React.useEffect(() => {
    if (focusRef.current) {
      requestReveal(true);
    }
  }, [hostTop, pinnedHeight, requestReveal]);
}

export function isHostViewport(message: unknown): message is IHostViewport {
  const report = message as Partial<IHostViewport> | undefined;
  return report?.type === 'host-viewport' && typeof report.top === 'number' && typeof report.height === 'number';
}

/** The band of the frame the host shows, in the frame's coordinates, as a `host-viewport` report carries it. */
export type IHostViewportArea = Pick<IHostViewport, 'top' | 'height'>;

const NOT_REPORTED: IHostViewportArea = { top: 0, height: 0 };

/**
 * The band of the frame the host last reported in view: how far the frame's top edge is scrolled out
 * of view, and how far the band reaches down from there; both 0 before a report reaches this part. A
 * host that scrolls the frame reports on every scroll and resize, and moves a new screen to its top.
 */
export function useHostViewport(): IHostViewportArea {
  const [area, setArea] = React.useState<IHostViewportArea>(NOT_REPORTED);

  React.useEffect(() => {
    const onParentMessage = (event: Event) => {
      const message = (event as CustomEvent).detail;
      if (isHostViewport(message)) {
        const next = { top: message.top, height: message.height };
        setArea((current) => (current.top === next.top && current.height === next.height ? current : next));
      }
    };

    window.addEventListener('content-sync-parent-message', onParentMessage);
    return () => {
      window.removeEventListener('content-sync-parent-message', onParentMessage);
    };
  }, []);

  return area;
}

/** The rendered height of an element, followed as it changes; 0 where it cannot be measured. */
export function useElementHeight(elementRef: React.RefObject<HTMLElement | null>): number {
  const [height, setHeight] = React.useState(0);

  React.useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }
    setHeight(element.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => setHeight(element.getBoundingClientRect().height));
    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef]);

  return height;
}
